"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Avatar = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const iconista_1 = tslib_1.__importDefault(require("iconista"));
const use_t_1 = require("use-t");
const Link_1 = require("../Link");
const context_1 = require("../../styles/context");
const { useState } = React;
const defaultWidth = 32;
const sizes = [10, 16, 20, 24, 32, 40, 48, 64, 128, 256, 512];
const defaultSize = 4;
const fontSizeFactor = 0.5;
const blockClass = (0, nano_theme_1.rule)({
    ...nano_theme_1.lightTheme.font.ui1.mid,
    pos: 'relative',
    d: 'flex',
    flex: '0 0',
    alignItems: 'center',
    justifyContent: 'center',
    ov: 'hidden',
    w: defaultWidth + 'px',
    h: defaultWidth + 'px',
    lh: defaultWidth + 'px',
    bdrad: '50%',
    bg: nano_theme_1.lightTheme.color.color[0],
    col: '#fff',
    ta: 'center',
    fz: 32 * fontSizeFactor + 'px',
    mar: 0,
    pad: 0,
    bd: 0,
    cur: 'pointer',
    userSelect: 'none',
    '&:hover': {
        col: '#fff',
    },
});
const hoverableClass = (0, nano_theme_1.rule)({
    '&:hover': {
        mar: '-2px',
    },
});
const squareClass = (0, nano_theme_1.rule)({
    bdrad: '8%',
});
const imgClass = (0, nano_theme_1.rule)({
    d: 'block',
    objectFit: 'cover',
    w: defaultWidth + 'px',
    h: defaultWidth + 'px',
});
const emojiClass = (0, nano_theme_1.rule)({
    bg: 'transparent',
    fz: defaultWidth + 'px',
    lh: defaultWidth + 'px',
});
const renderImg = (width, src, onError) => {
    const props = {
        className: imgClass,
        src,
        onError,
    };
    if (width) {
        props.style = {
            width,
            height: width,
        };
    }
    // biome-ignore lint/a11y/useAltText: alt is spread from props
    return React.createElement("img", { ...props });
};
const Avatar = (allProps) => {
    const { id, href, name, emoji: emojiImmutable, size, src, width, grey, lightGrey, transparent, className, square, rounded, isPrivate, isOP, badge, hover, color, icon, del, bottomRight, noHover, bold, letters, ...rest } = allProps;
    let emoji = emojiImmutable;
    if (del)
        emoji = '␡';
    const [t] = (0, use_t_1.useT)();
    const [error, setError] = useState(false);
    const styles = (0, context_1.useStyles)();
    const showText = error || !src;
    const props = rest;
    props.className =
        (className || '') +
            blockClass +
            (hover ? hoverableClass : '') +
            (square || emoji ? squareClass : '') +
            (emoji ? emojiClass : '');
    props.style = { ...props.style };
    if (bold) {
        props.style.fontWeight = 600;
    }
    if (noHover) {
        props.style.cursor = 'default';
    }
    props.style.flex = `0 0 ${width || 32}px`;
    if (transparent) {
        props.style.background = 'transparent';
    }
    else if (lightGrey) {
        props.style.background = styles.g(0.1, 0.08);
        props.style.color = styles.g(0.2, 0.4);
        props.style.fill = styles.g(0.2, 0.4);
    }
    else if (grey || del) {
        props.style.background = styles.g(0.4);
        props.style.color = styles.g(0.9);
        props.style.fill = styles.g(0.9);
    }
    else if (color) {
        props.style.background = color;
    }
    const computedWidth = width || (size ? sizes[defaultSize + (size || 0)] : 0);
    if (computedWidth) {
        props.style.width = computedWidth;
        props.style.height = computedWidth;
        props.style.lineHeight = emoji ? `${computedWidth * 1.07}px` : `${computedWidth}px`;
        props.style.fontSize = emoji ? `${computedWidth * 0.85}px` : `${computedWidth * fontSizeFactor}px`;
        if (computedWidth < 24) {
            props.style.fontWeight = 'bold';
            props.style.lineHeight = computedWidth + 1 + 'px';
        }
    }
    if (showText && name && !emoji && !(grey || del) && !lightGrey && !transparent && !color) {
        props.style.background = styles.col.hash(id || name) + '';
    }
    if (rounded) {
        props.style.borderRadius = '25%';
    }
    if ((grey || del || lightGrey) && !name) {
        props.style.opacity = 0.3;
    }
    let element = icon
        ? icon
        : showText
            ? emoji
                ? emoji
                : typeof name === 'string' && name.length > 0
                    ? name.slice(0, letters || 2).trim()
                    : ''
            : renderImg(computedWidth, src, () => setError(true));
    if (href) {
        element = (React.createElement(Link_1.Link, { a: true, ...props, to: href }, element));
    }
    else {
        element = React.createElement("span", { ...props }, element);
    }
    if (isPrivate || badge || isOP || !!bottomRight) {
        const size = computedWidth || 32;
        let lock;
        let badgeElement;
        let subAvatarElement;
        if (isPrivate) {
            const lockSize = size * 0.5;
            const iconSize = lockSize * 0.75;
            const lockStyle = {
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                top: size * -0.1,
                left: size * -0.1,
                background: props.style.background || styles.col.hash(id || name || '') + '' || styles.col.get('error', 'solid-1'),
                width: lockSize + 'px',
                height: lockSize + 'px',
                borderRadius: '50%',
                fill: '#fff',
                color: '#fff',
            };
            lock = (React.createElement("span", { style: lockStyle, title: t('Private') },
                React.createElement(iconista_1.default, { set: "atlaskit", icon: "lock-filled", width: iconSize, height: iconSize, style: { margin: `${size <= 40 ? -1 : 0}px 0 0` } })));
        }
        if (badge || isOP) {
            const badgeSize = size * 0.25;
            badgeElement = (React.createElement("span", { style: {
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    top: size * -0.035,
                    right: size * -0.035,
                    background: styles.col.get('warning', 'el-2'),
                    border: `1px solid ${styles.bg}`,
                    width: badgeSize + 'px',
                    height: badgeSize + 'px',
                    borderRadius: '50%',
                    fill: styles.bg + '',
                    color: styles.bg + '',
                } }));
        }
        if (bottomRight) {
            const subAvatarSize = size * 0.6;
            subAvatarElement = (React.createElement("span", { style: {
                    display: 'inline-block',
                    position: 'absolute',
                    bottom: -0.2 * subAvatarSize,
                    right: -0.3 * subAvatarSize,
                } }, bottomRight));
        }
        element = (React.createElement("span", { style: { display: 'inline-block', position: 'relative' } },
            element,
            lock,
            badgeElement,
            subAvatarElement));
    }
    return element;
};
exports.Avatar = Avatar;
